import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Register.css';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    updateLocalStorageFromQueryParams,
} from '../../Helper/storageUtils';
import Billede1 from '../../Images/Billede1.jpg';
import HeroSectionV2 from '../../components/HeroSectionV2';

interface Country {
    code: string;
}

const Register: React.FC = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        updateLocalStorageFromQueryParams(params, ['eventId', 'entry', 'club', 'media', 'team']);
    }, [location]);

    const buildLoginQueryParams = () => {
        const params = new URLSearchParams(location.search);

        const eventId =
            params.get("eventId") ||
            params.get("eventid") ||
            localStorage.getItem("eventId") ||
            localStorage.getItem("eventid");

        const entry =
            params.get("entry") ||
            localStorage.getItem("entry");

        const loginParams = new URLSearchParams();

        if (eventId) {
            loginParams.set("eventId", eventId);
        }

        if (entry) {
            loginParams.set("entry", entry === "true" ? "team" : entry);
        }

        return loginParams.toString();
    };

    const [countries, setCountries] = useState<Country[]>([]);
    const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/countries`);
                setCountries(response.data);
            } catch (error) {
                console.error('Failed to fetch countries', error);
            }
        };
        fetchCountries();
    }, []);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        dateOfBirth: '',
        countryCode: '',
        uploadImageBase64: ''
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setPreviewImage(result);
            setFormData(prev => ({
                ...prev,
                uploadImageBase64: result
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleRegister = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (formData.password !== formData.confirmPassword) {
            setErrorMessage(language === 'en' ? 'Passwords do not match.' : 'Kodeordene er ikke ens.');
            return;
        }

        const age = getAge(formData.dateOfBirth);
        if (age < 12) {
            setErrorMessage(
                language === 'en'
                    ? 'You must be at least 12 years old to register.'
                    : 'Du skal være mindst 12 år for at oprette en konto.'
            );
            return;
        }

        const params = new URLSearchParams(location.search);
        const entry = params.get("entry")?.toLowerCase();

        try {
            const payload = {
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                dateOfBirth: formData.dateOfBirth,
                country: formData.countryCode,
                uploadImageBase64: formData.uploadImageBase64 || null,
                entry: entry
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users/register`,
                payload
            );

            if (response.status === 201) {
                setSuccessMessage(
                    language === 'en'
                        ? 'Account created successfully! Redirecting to login...'
                        : 'Kontoen er oprettet! Omdirigerer til login...'
                );

                setTimeout(() => {
                    const params = buildLoginQueryParams();
                    navigate(params ? `/login?${params}` : '/login');
                }, 2500);
            }
        } catch (error: any) {
            setErrorMessage(
                error.response?.data?.message ||
                (language === 'en'
                    ? 'An error occurred during registration.'
                    : 'Der opstod en fejl under registreringen.')
            );
        }
    };

    const getAge = (dob: string) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    return (
        <div className="home">
            <HeroSectionV2
                titleEn="Welcome to Badminton Denmark"
                titleDa="Velkommen til Badminton Danmark"
                descriptionEn="Login or sign up to access all the features!"
                descriptionDa="Log ind eller opret en profil for at få adgang til alle funktioner!"
                imageSrc={Billede1}
            />

            <div className="register">
                <h1>{language === 'en' ? 'Create an Account' : 'Opret Profil'}</h1>

                <form onSubmit={handleRegister} className="register-form">
                    <div className="form-row">
                        <div className="form-group full-width">
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Email' : 'E-mail'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="password" name="password" value={formData.password} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Password' : 'Kodeord'}</label>
                        </div>
                        <div className="form-group">
                            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Confirm Password' : 'Bekræft Kodeord'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                            <label>{language === 'en' ? 'First Name' : 'Fornavn'}</label>
                        </div>

                        <div className="form-group">
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Last Name' : 'Efternavn'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Phone' : 'Telefon'}</label>
                        </div>

                        <div className="form-group">
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleChange}
                                required
                                max={new Date().toISOString().split('T')[0]}
                            />
                            <label>{language === 'en' ? 'Date of Birth' : 'Fødselsdato'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <select name="countryCode" value={formData.countryCode} onChange={handleChange} required>
                                <option value="">
                                    {language === 'en' ? 'Select country' : 'Vælg land'}
                                </option>
                                {countries.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.code}
                                    </option>
                                ))}
                            </select>
                            <label>{language === 'en' ? 'Country' : 'Land'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            {previewImage && (
                                <div style={{ marginBottom: 10 }}>
                                    <img
                                        src={previewImage}
                                        alt="Preview"
                                        style={{
                                            width: 120,
                                            height: 120,
                                            objectFit: 'cover',
                                            borderRadius: '50%'
                                        }}
                                    />
                                </div>
                            )}

                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                            <label>{language === 'en' ? 'Profile Picture (optional)' : 'Profilbillede (valgfrit)'}</label>
                        </div>
                    </div>

                    <button type="submit" className="register-button">
                        {language === 'en' ? 'Sign Up' : 'Opret Profil'}
                    </button>

                    {errorMessage && <p className="error">{errorMessage}</p>}
                    {successMessage && <p className="success">{successMessage}</p>}
                </form>
            </div>
        </div>
    );
};

export default Register;